import type { Conference } from '../../types.js';
import { RSNA_RULESET } from '../conference/rsnaRules.js';
import { getOncologyProfile } from '../conference/oncologyRules.js';
import {
  callForAbstractsGuidance,
  getISMRMTypeGuidance,
  ISMRM_2027_RULES,
} from '../llm/prompts/ismrmData.js';

type ReviewConference = Exclude<Conference, 'IMAGE' | 'JACC' | 'ESC'>;

export function getConferenceBlindReviewRules(conference: ReviewConference): string {
  if (conference === 'ISMRM') {
    return [
      `${ISMRM_2027_RULES.version} platform-configured rule set (verified 2026-10-11; authors must recheck the official call):`,
      callForAbstractsGuidance,
      ...ISMRM_2027_RULES.abstractTypes.map(getISMRMTypeGuidance),
      '- Assess anonymity, ethics, consent, conflicts, funding, and reporting-guideline fit.',
    ].join('\n');
  }

  if (conference === 'RSNA') {
    return [
      `RSNA ${RSNA_RULESET.meetingYear} current platform rule set (${RSNA_RULESET.status}; authors must recheck the official call):`,
      `- Science abstract: ${RSNA_RULESET.science.abstractCharactersExcludingSpaces} characters excluding spaces; Clinical Relevance: ${RSNA_RULESET.science.clinicalRelevanceCharacters} characters.`,
      `- Science sections: ${RSNA_RULESET.science.sections.join(', ')}.`,
      `- Education exhibit: ${RSNA_RULESET.education.abstractCharacters} characters; sections: ${RSNA_RULESET.education.sections.join(', ')}.`,
      '- Assess anonymity, category/format fit, ethics, statistics, and source-grounded claims.',
    ].join('\n');
  }

  if (conference === 'ER') {
    return [
      'ECR 2027 ESR rule set (checked 2026-09-29; authors must recheck the official call):',
      '- Source: https://www.myesr.org/congress/submit/abstract-submission/ and https://www.myesr.org/congress/submit/abstract-submission/writing-tips/',
      '- Abstract body: maximum 280 words. The platform impact statement: 50 words and synopsis: 100 words are drafting aids, not separate ESR fields.',
      '- Title: no full stop, trade names, or special symbols.',
      '- The first four ESR fields cover purpose/learning objective, methods/background, results/findings, and conclusions; limitations, ethics approval, and funding are conditional portal fields.',
      '- Use British English; spell out numbers below 10; each section must be a complete paragraph ending with a full stop.',
      '- Maximum 9 authors; up to 10 images for posters only; up to 3 keywords per column with one keyword per category mandatory.',
      '- No references, acknowledgements, graphics, tables, or figures in the abstract body; writing/editing AI is disclosed via the portal checkbox, while AI-generated research results belong in Methods.',
      '- Case reports are ineligible. Assess patient privacy, ethics, conflicts of interest, conditional funding/limitations, and study-specific reporting requirements.',
    ].join('\n');
  }

  const profile = getOncologyProfile(conference);
  const submissionRules = profile.submissionTypes
    .map((type) => `${type.id}: ${type.requiredSections.join(', ')}`)
    .join('; ');
  const conferenceSpecificRules =
    conference === 'ASCO'
      ? '- ASCO tables: at most one table with no more than 10 rows.'
      : '- ESMO limits: at most one table of no more than 600 characters; no more than 20 authors; clinical or translational patient-data abstracts require an independent practicing physician or investigator as presenter; AI use affecting research data must be described in Methods.';
  return [
    `${profile.ruleVersion} platform profile (authors must recheck the official call):`,
    `- Combined title, abstract body, and table text: maximum ${profile.characterLimitExcludingSpaces} characters excluding spaces.`,
    `- Submission contracts: ${submissionRules}.`,
    `- Presentation preference: ${profile.presentationPreference}.`,
    '- Trial-in-progress abstracts must not include results or preliminary data. Case reports and figures are not allowed; at least one keyword is required.',
    conferenceSpecificRules,
    '- Assess anonymity, patient-data handling, ethics, conflicts, funding, AI-method disclosure, study maturity, and reporting-guideline fit.',
  ].join('\n');
}
